import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setSelectedWeekViewDayToggle,
  setSelectedDate,
  setSelectedDaysOfWeekToShowInCurrentWeek,
  setSelectedNumberOfDaysToShowInWeek,
} from '../../ducks/calendarActions';

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  max-width: calc(100% - ${(props) => (props.isPrimaryAnimatedSideBarOpen ? 384 : 0)}px - 48px);
  padding: 8px;
  padding-bottom: 8px;
  background: white;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  ${(props) => props.viewType === 'weekView' && 'position: fixed;'}
  ${(props) => props.viewType === 'weekView' && 'bottom: 0;'}

  @media screen and (max-width: 1023px) {
    max-width: calc(100% - ${(props) => (props.isPrimaryAnimatedSideBarOpen ? 300 : 0)}px - 48px);
  }
`;

const WeeksWrapper = styled('div')`
  display: flex;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const WeekContainer = styled('div')`
  display: flex;
  padding: 4px 16px;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 4px;
  background: ${(props) => props.isSelected && '#F8F8F8'};
   :hover {
      background: #F8F8F8;
    }
`;

const ArrowsWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
`;

const ArrowButton = styled('button')`
  border: none;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  min-width: 28px;
  height: 28px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  border-radius: 4px;
  padding: 0;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  &:hover {
    background: ${(props) => (props.disabled ? 'transparent' : '#F0F0F0')};
  }
`;

const WeekViewDayToggleFooter = () => {
  const dispatch = useDispatch();
  const weeksRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const animatedSidebarContainerWidth = useSelector(
    (state) => state.calendar.data.animatedSidebarContainerWidth,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const viewType = useSelector((state) => state.calendar.data.viewType);

  const checkOverflow = useCallback(() => {
    const el = weeksRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  useEffect(() => {
    checkOverflow();
  }, [isPrimaryAnimatedSideBarOpen, checkOverflow]);

  const stopScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  const startScroll = useCallback((direction) => {
    stopScroll();
    const step = direction === 'left' ? -4 : 4;
    scrollIntervalRef.current = setInterval(() => {
      const el = weeksRef.current;
      if (!el) return;
      el.scrollLeft += step;
      checkOverflow();
    }, 30);
  }, [stopScroll, checkOverflow]);

  useEffect(() => () => stopScroll(), [stopScroll]);

  const options = [
    'Everyday',
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const getFirstToggleDayOfCurrentMonth = (targetWeekdayName) => {
    const startOfCurrentMonth = moment(selectedDate).startOf('month');

    while (startOfCurrentMonth.format('dddd') !== targetWeekdayName) {
      startOfCurrentMonth.add(1, 'day');
    }

    return startOfCurrentMonth.toDate();
  };

  return (
    <Wrapper
      viewType={viewType}
      animatedSidebarContainerWidth={animatedSidebarContainerWidth}
      isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
    >
      <WeeksWrapper ref={weeksRef} onScroll={checkOverflow}>
        {options.map((option) => (
          <WeekContainer
            key={option}
            isSelected={selectedWeekViewDayToggle === option}
            onClick={() => {
              dispatch(setSelectedWeekViewDayToggle(option));
              if (option !== 'Everyday') {
                dispatch(
                  setSelectedDate(getFirstToggleDayOfCurrentMonth(option)),
                );
              }
              if (option === 'Everyday') {
                if (selectedWeekViewDayToggle !== 'Everyday') {
                  /** reset filters */
                  dispatch(setSelectedNumberOfDaysToShowInWeek(7));
                  dispatch(setSelectedDaysOfWeekToShowInCurrentWeek(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Empty Days', 'Weekends']));
                }
              }
            }}
          >
            <Text fontSize='16px' fontWeight={selectedWeekViewDayToggle === option ? 700 : 400} color={selectedWeekViewDayToggle !== option && '#9E9E9E'}>
              {option}
            </Text>
            <IcomoonReact
              iconSet={iconSet}
              size={10}
              icon='down-arrow'
              color={selectedWeekViewDayToggle !== option ? '#9E9E9E' : '#444444'}
            />
          </WeekContainer>
        ))}
      </WeeksWrapper>
      {(showLeftArrow || showRightArrow) && (
        <ArrowsWrapper>
          <ArrowButton
            disabled={!showLeftArrow}
            onMouseDown={() => showLeftArrow && startScroll('left')}
            onMouseUp={stopScroll}
            onMouseLeave={stopScroll}
            onTouchStart={() => showLeftArrow && startScroll('left')}
            onTouchEnd={stopScroll}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='left-arrow'
              color='#444444'
            />
          </ArrowButton>
          <ArrowButton
            disabled={!showRightArrow}
            onMouseDown={() => showRightArrow && startScroll('right')}
            onMouseUp={stopScroll}
            onMouseLeave={stopScroll}
            onTouchStart={() => showRightArrow && startScroll('right')}
            onTouchEnd={stopScroll}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='right-arrow'
              color='#444444'
            />
          </ArrowButton>
        </ArrowsWrapper>
      )}
    </Wrapper>
  );
};

export default WeekViewDayToggleFooter;
